'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/Dialog';
import { useErrorDialog } from './ui/ErrorDialog';
import { useToast } from '../contexts';
import { useAuth } from '../contexts/AuthContext';
import { apiLoginVerify, VerifyPayload, forgotPassword, googleLogin } from '../services/authServices';
import { renderGoogleButton, isGoogleEnabled } from '../utils/google';

type Step = 'login' | 'phone' | 'code' | 'forgot' | 'forgot-code' | 'forgot-newpass' | 'renew';

type Form = {
  login: string;
  password: string;
  phoneDigits: string;
  rememberMe: boolean;
  forgotContact: string;
  newPassword: string;
  confirmPassword: string;
};

type BetaLoginDialogProps = {
  open: boolean;
  downloadUrl?: string;
  resultImageSrc?: string;
};

import { onlyDigits, COUNTRIES, type Country, toE164, formatBR, formatPhone, isProbablyPhone, phonePlaceholder } from '../utils/phone';
import CountrySelector from './CountrySelector';

const REMEMBER_KEY = 'liberty_remember';

/* ---------------- OTP Input ---------------- */

function OTPInput({
  length = 6,
  value,
  onChange,
  disabled
}: {
  length?: number;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const vals = useMemo(() => {
    const clean = onlyDigits(value).slice(0, length);
    return Array.from({ length }, (_, i) => clean[i] ?? '');
  }, [value, length]);

  const focusAt = (i: number) => {
    refs.current[i]?.focus();
    refs.current[i]?.select();
  };

  const setChar = (i: number, char: string) => {
    const clean = onlyDigits(char).slice(-1);
    const arr = [...vals];
    arr[i] = clean;
    onChange(arr.join(''));
    if (clean && i < length - 1) focusAt(i + 1);
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (vals[i]) {
        setChar(i, '');
      } else if (i > 0) {
        focusAt(i - 1);
        const arr = [...vals];
        arr[i - 1] = '';
        onChange(arr.join(''));
      }
    }
    if (e.key === 'ArrowLeft' && i > 0) focusAt(i - 1);
    if (e.key === 'ArrowRight' && i < length - 1) focusAt(i + 1);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = onlyDigits(e.clipboardData.getData('text')).slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    const idx = Math.min(text.length - 1, length - 1);
    requestAnimationFrame(() => focusAt(idx));
  };

  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={vals[i]}
          disabled={disabled}
          onPaste={onPaste}
          onKeyDown={(e) => onKeyDown(i, e)}
          onChange={(e) => setChar(i, e.target.value)}
          className={[
            'h-12 w-11 rounded-xl border border-white/10 bg-white/5 text-center text-xl font-extrabold text-white',
            'outline-none backdrop-blur-xl',
            'focus:border-[#00E1D7]/60 focus:ring-2 focus:ring-[#00E1D7]/20',
            disabled ? 'opacity-60' : 'hover:bg-white/10'
          ].join(' ')}
        />
      ))}
    </div>
  );
}

/* ---------------- Dialog ---------------- */

export default function BetaLoginDialog({ open }: BetaLoginDialogProps) {
  const { showError } = useErrorDialog();
  const { setOpenVerify } = useToast();
  const auth = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);
  // fluxo por codigo (esqueci / renovacao): pra onde o codigo foi (mascarado)
  const [codeDest, setCodeDest] = useState<{ channel: string; destination: string } | null>(null);
  // renovacao obrigatoria de senha (conta antiga: senhas alteradas no teste dos bots)
  const [renewMode, setRenewMode] = useState(false);

  // Google button rendering — guard contra re-render duplicado do GSI
  const googleBtnRef = useRef<HTMLDivElement | null>(null);
  const googleRenderedRef = useRef(false);

  const [form, setForm] = useState<Form>(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        const { login } = JSON.parse(saved);
        return { login: login || '', password: '', phoneDigits: '', rememberMe: true, forgotContact: '', newPassword: '', confirmPassword: '' };
      }
    } catch { /* ignore */ }
    return { login: '', password: '', phoneDigits: '', rememberMe: false, forgotContact: '', newPassword: '', confirmPassword: '' };
  });

  const [country, setCountry] = useState<Country>(COUNTRIES.find(c => c.iso === 'br')!);

  const contactRef = useRef<HTMLInputElement | null>(null);

  const applyMaskedContact = (raw: string) => (isProbablyPhone(raw) ? formatPhone(country, raw) : raw);

  const onChangeLoginSmart = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    const nextTyped = e.target.value;
    const nextRaw = isProbablyPhone(nextTyped) ? onlyDigits(nextTyped) : nextTyped;

    const caret = el.selectionStart ?? nextTyped.length;
    setForm((s) => ({ ...s, login: nextRaw }));

    requestAnimationFrame(() => {
      const input = contactRef.current;
      if (!input) return;
      input.focus();
      const len = input.value.length;
      const pos = Math.min(caret, len);
      try {
        input.setSelectionRange(pos, pos);
      } catch { }
    });
  };

  // derivados
  const loginRaw = form.login;
  const loginIsPhone = isProbablyPhone(loginRaw);
  const loginPhoneE164 = loginIsPhone ? toE164(country, loginRaw) : '';
  const loginId = !loginIsPhone ? loginRaw.trim() : '';

  const extraPhoneE164 = toE164(country, form.phoneDigits);
  const otpValid = otp.length === 6;

  const applyCooldown = (seconds?: number) => {
    const s = typeof seconds === 'number' && seconds > 0 ? seconds : 60;
    setCooldown((cur) => Math.max(cur, s));
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // === Google sign-in: lida com credential vindo do botao GSI ===
  const handleGoogleCredential = async (credential: string) => {
    setError(null);
    setLoading(true);
    try {
      const res: any = await googleLogin({ IdToken: credential });
      const st = String(res?.status ?? res?.Status ?? '');

      // Conta nao existe — manda pra /cadastro pra completar setup (ID/senha)
      if (st === 'NEEDS_SETUP') {
        closeAll();
        navigate('/cadastro');
        return;
      }

      // Conta existe — JWT direto
      if (st === 'OK' && res?.token) {
        if (form.rememberMe) {
          localStorage.setItem(REMEMBER_KEY, JSON.stringify({ login: res.username || '' }));
        }
        auth.login({
          username: res.username || '',
          token: res.token,
          expiration: res.expiration,
        });
        closeAll();
        navigate('/dashboard');
        return;
      }

      const msg = res?.message || res?.Message || 'Nao foi possivel entrar com Google.';
      setError(msg);
      showError({ title: 'Falha no login Google', message: msg });
    } catch (e: any) {
      const msg = e?.Message || e?.message || 'Nao foi possivel entrar com Google.';
      setError(msg);
      showError({ title: 'Falha no login Google', message: msg });
    } finally {
      setLoading(false);
    }
  };

  // Renderiza o botao Google quando o dialog abre no step 'login'
  useEffect(() => {
    if (!open || step !== 'login' || !isGoogleEnabled()) return;
    if (!googleBtnRef.current || googleRenderedRef.current) return;
    googleRenderedRef.current = true;
    renderGoogleButton(googleBtnRef.current, handleGoogleCredential, { text: 'signin_with' }).catch(() => {
      googleRenderedRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  useEffect(() => {
    if (!open) {
      setStep('login');
      setLoading(false);
      setError(null);
      setOtp('');
      setCooldown(0);
      googleRenderedRef.current = false; // re-render quando reabrir

      // Preservar login salvo
      setForm((prev) => {
        try {
          const saved = localStorage.getItem(REMEMBER_KEY);
          if (saved) {
            const { login } = JSON.parse(saved);
            return { login: login || '', password: '', phoneDigits: '', rememberMe: true, forgotContact: '', newPassword: '', confirmPassword: '' };
          }
        } catch { /* ignore */ }
        return { login: prev.rememberMe ? prev.login : '', password: '', phoneDigits: '', rememberMe: prev.rememberMe, forgotContact: '', newPassword: '', confirmPassword: '' };
      });
    }
  }, [open]);

  const setField = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((s) => ({ ...s, [key]: value }));
  };

  const closeAll = () => setOpenVerify(false);

  const getStatus = (res: any) => String(res?.Status ?? res?.status ?? '');
  const getMessage = (res: any) => String(res?.Message ?? res?.message ?? '');
  // O erro chega como o corpo do FastAPI: {detail: {Status, Message, ...}}.
  // Antes so' se olhava e.payload (inexistente) e a mensagem do servidor sumia.
  const errBody = (e: any): any =>
    (e?.detail && typeof e.detail === 'object') ? e.detail : (e?.payload ?? e ?? {});
  const errMsg = (e: any, fallback: string) => {
    const b = errBody(e);
    return String(b?.Message || b?.message || (typeof e?.detail === 'string' ? e.detail : '') || e?.message || fallback);
  };
  const destOf = (b: any) => ({
    channel: String(b?.Channel ?? b?.channel ?? ''),
    destination: String(b?.Destination ?? b?.destination ?? ''),
  });

  const handleLoginSuccess = (res: any) => {
    const t = res?.token ?? null;
    const exp = res?.expiration ?? null;

    const user =
      String(res?.username ?? res?.Username ?? (loginIsPhone ? loginPhoneE164 : loginId) ?? '').trim();

    // Salvar login se rememberMe
    if (form.rememberMe) {
      localStorage.setItem(REMEMBER_KEY, JSON.stringify({ login: form.login }));
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }

    // Auth state
    if (t && user) {
      auth.login({ username: user, token: t, expiration: exp || undefined });
    }

    // Fechar e ir pro dashboard
    closeAll();
    navigate('/dashboard');
  };

  const validateLoginStep = () => {
    if (!form.login.trim()) return 'Informe seu ID, e-mail ou celular.';
    if (loginIsPhone && !loginPhoneE164) return 'Enter a valid phone number / Informe um celular válido.';
    if (!loginIsPhone && loginId.length < 3) return 'Informe seu ID.';
    if (!form.password) return 'Informe sua senha.';
    return null;
  };

  const callUnified = async (payload: VerifyPayload) => {
    const res = await apiLoginVerify(payload);
    const st = getStatus(res);

    if (st === 'NEED_PHONE') {
      setStep('phone');
      return;
    }

    if (st === '1') {
      setStep('code');
      setOtp('');
      applyCooldown(res?.retryAfterSeconds ?? 60);
      return;
    }

    if (st === '2') {
      if (res?.token) {
        handleLoginSuccess(res);
        return;
      }
      const res2 = await apiLoginVerify({
        Username: loginId,
        Password: form.password,
        Cellphone: extraPhoneE164,
        Code: null
      });
      handleLoginSuccess(res2);
      return;
    }

    if (res?.token || st === 'OK' || st === '') {
      handleLoginSuccess(res);
      return;
    }

    const msg = getMessage(res) || 'Nao foi possivel continuar.';
    throw new Error(msg);
  };

  // ========== actions ==========
  const doLogin = async () => {
    setError(null);

    const err = validateLoginStep();
    if (err) {
      setError(err);
      return;
    }

    try {
      setLoading(true);
      await callUnified({
        Username: loginIsPhone ? loginPhoneE164 : loginId,
        Password: form.password,
        Cellphone: null,
        Code: null
      });
    } catch (e: any) {
      const body = errBody(e);

      // Conta antiga: precisa criar senha nova. O servidor ja' mandou o codigo
      // (celular ou e-mail) -- mostra pra onde foi e segue pro input de codigo.
      if (getStatus(body) === 'PASSWORD_RENEW') {
        setRenewMode(true);
        setCodeDest(destOf(body));
        setField('forgotContact', String(body?.Contact ?? body?.contact ?? form.login));
        setOtp('');
        applyCooldown(body?.retryAfterSeconds ?? 60);
        setStep('renew');
        return;
      }

      const msg = errMsg(e, 'Falha no login. Verifique os dados e tente novamente.');
      if (typeof body?.retryAfterSeconds === 'number') applyCooldown(body.retryAfterSeconds);

      setError(msg);
      showError({ title: 'Falha', message: msg });
    } finally {
      setLoading(false);
    }
  };

  const requestSmsForLink = async () => {
    setError(null);

    if (!loginId) {
      setError('Informe um ID valido.');
      return;
    }
    if (!extraPhoneE164) {
      setError('Enter a valid phone number / Informe um celular válido.');
      return;
    }

    try {
      setLoading(true);
      await callUnified({
        Username: loginId,
        Password: form.password,
        Cellphone: extraPhoneE164,
        Code: null
      });
    } catch (e: any) {
      const payload = e?.payload;
      const msg = payload?.Message || payload?.message || e?.message || 'Nao foi possivel enviar o SMS.';

      if (e?.status === 429) {
        if (typeof payload?.retryAfterSeconds === 'number') applyCooldown(payload.retryAfterSeconds);
      }

      setError(msg);
      showError({ title: 'Falha ao enviar SMS', message: msg });
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || loading) return;
    await requestSmsForLink();
  };

  const verifyAndLogin = async () => {
    setError(null);

    if (!otpValid) {
      setError('Digite o codigo completo (6 digitos).');
      return;
    }

    try {
      setLoading(true);
      await callUnified({
        Username: loginId,
        Password: form.password,
        Cellphone: extraPhoneE164,
        Code: otp
      });
    } catch (e: any) {
      const payload = e?.payload;
      const msg = payload?.Message || payload?.message || e?.message || 'Falha na verificacao.';

      if (e?.status === 429) {
        if (typeof payload?.retryAfterSeconds === 'number') applyCooldown(payload.retryAfterSeconds);
      }

      setError(msg);
      showError({ title: 'Falha na verificacao', message: msg });
    } finally {
      setLoading(false);
    }
  };

  const goBackToLogin = () => {
    setError(null);
    setOtp('');
    setCooldown(0);
    setRenewMode(false);
    setCodeDest(null);
    setStep('login');
  };

  // ========== forgot password ==========
  const forgotContactE164 = isProbablyPhone(form.forgotContact)
    ? toE164(country, form.forgotContact)
    : '';
  const forgotContactValue = isProbablyPhone(form.forgotContact)
    ? forgotContactE164
    : form.forgotContact.trim();

  const doForgotRequest = async () => {
    setError(null);
    if (!form.forgotContact.trim()) {
      setError('Informe seu ID, e-mail ou celular.');
      return;
    }
    if (isProbablyPhone(form.forgotContact) && !forgotContactE164) {
      setError('Enter a valid phone number / Informe um celular válido.');
      return;
    }
    try {
      setLoading(true);
      const res = await forgotPassword({ contact: forgotContactValue });
      const st = getStatus(res);
      if (st === '1' || st === 'CODE_SENT') {
        setCodeDest(destOf(res));
        setStep('forgot-code');
        setOtp('');
        applyCooldown(res?.retryAfterSeconds ?? 60);
      } else if (st === 'EMAIL_SENT') {
        // Conta sem celular mas com email -> link de reset enviado por email.
        // Volta pro login e mostra a mensagem (o fluxo continua no link do email).
        setStep('login');
        showError({
          title: 'Verifique seu email',
          message: getMessage(res) || 'Enviamos um link de redefinição para o seu email. Verifique sua caixa de entrada (e o spam).',
        });
      } else if (st === 'NOT_FOUND') {
        setError('Conta não encontrada. Verifique o ID, email ou celular.');
      } else {
        setError(getMessage(res) || 'Erro ao solicitar reset.');
      }
    } catch (e: any) {
      const body = errBody(e);
      if (getStatus(body) === 'NOT_FOUND') setError('Conta não encontrada. Verifique o ID, e-mail ou celular.');
      else setError(errMsg(e, 'Erro ao solicitar o código.'));
      if (typeof body?.retryAfterSeconds === 'number') applyCooldown(body.retryAfterSeconds);
    } finally {
      setLoading(false);
    }
  };

  const doForgotVerifyCode = async () => {
    setError(null);
    if (!otpValid) {
      setError('Digite o código completo (6 dígitos).');
      return;
    }
    try {
      setLoading(true);
      const res = await forgotPassword({ contact: forgotContactValue, code: otp });
      const st = getStatus(res);
      if (st === '2' || st === 'CODE_OK') {
        setStep('forgot-newpass');
      } else {
        setError(getMessage(res) || 'Código inválido.');
      }
    } catch (e: any) {
      setError(errMsg(e, 'Código inválido ou expirado.'));
    } finally {
      setLoading(false);
    }
  };

  const doForgotSavePassword = async () => {
    setError(null);
    if (form.newPassword.length < 6) {
      setError('Senha deve ter no mínimo 6 caracteres.');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }
    try {
      setLoading(true);
      const res = await forgotPassword({
        contact: forgotContactValue,
        code: otp,
        newPassword: form.newPassword,
      });
      const st = getStatus(res);
      if (st === 'OK' || st === 'PASSWORD_CHANGED') {
        showError(renewMode
          ? { title: 'Nova senha criada', message: 'Pronto! Sua nova senha está ativa. Entre com ela agora.' }
          : { title: 'Senha alterada', message: 'Sua senha foi alterada com sucesso. Faça login.' });
        setForm((s) => ({ ...s, password: '', newPassword: '', confirmPassword: '' }));
        goBackToLogin();
      } else {
        setError(getMessage(res) || 'Erro ao salvar nova senha.');
      }
    } catch (e: any) {
      setError(errMsg(e, 'Erro ao alterar senha.'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      if (step === 'login') doLogin();
      else if (step === 'renew') setStep('forgot-code');
      else if (step === 'phone') requestSmsForLink();
      else if (step === 'code' && otpValid) verifyAndLogin();
      else if (step === 'forgot') doForgotRequest();
      else if (step === 'forgot-code' && otpValid) doForgotVerifyCode();
      else if (step === 'forgot-newpass') doForgotSavePassword();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpenVerify}>
      <DialogContent
        onClose={closeAll}
        className="
          login-modal lb-dialog lb-login ornate-card relative overflow-auto sm:overflow-hidden text-white
          w-screen h-[100dvh] rounded-none p-5
          sm:w-[min(560px,92vw)] sm:h-auto sm:rounded-[1.4rem] sm:p-8
        "
      >
        <DialogHeader>
          <div className="flex flex-row items-center gap-3 px-1 pt-3">
            <img src="/assets/liberty/logo.webp?v=20260926" className="lb-login-logo" alt="Cabal Liberty" />
            <div className="flex flex-col justify-center">
              <DialogTitle className="lb-dialog-title">
                {step === 'login' ? 'Entrar'
                  : step === 'phone' ? 'Vincular celular'
                  : step === 'code' ? 'Verificar código'
                  : step === 'forgot' ? 'Recuperar senha'
                  : step === 'forgot-code' ? 'Verificar código'
                  : step === 'renew' ? 'Crie uma nova senha'
                  : 'Nova senha'}
              </DialogTitle>

              <DialogDescription className="text-white/65">
                {step === 'login' ? 'Entre com seu ID, e-mail ou celular e senha.'
                  : step === 'phone' ? 'Seu ID ainda não tem celular vinculado. Informe um número para validar.'
                  : step === 'code' ? `Enviamos um código para ${extraPhoneE164}. Digite abaixo.`
                  : step === 'forgot' ? 'Informe seus dados para recuperar o acesso.'
                  : step === 'forgot-code'
                    ? (codeDest?.channel === 'email' ? 'Digite o código que enviamos por e-mail.' : 'Digite o código que enviamos por SMS.')
                  : step === 'renew' ? 'Por segurança, sua conta precisa de uma nova senha.'
                  : 'Defina sua nova senha de acesso.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* CONTENT */}
        <div onKeyDown={handleKeyDown}>
          {step === 'login' ? (
            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <label className="text-xs font-semibold text-white/70">ID, e-mail ou celular</label>

                <div className="flex items-center gap-2">
                  {isProbablyPhone(form.login) && (
                    <CountrySelector value={country} onChange={setCountry} />
                  )}

                  <input
                    ref={contactRef}
                    name="username"
                    autoComplete="username"
                    value={applyMaskedContact(form.login)}
                    onChange={onChangeLoginSmart}
                    inputMode={isProbablyPhone(form.login) ? 'tel' : 'text'}
                    placeholder={isProbablyPhone(form.login) ? '(11) 91234-5678' : 'Seu ID ou e-mail'}
                    className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/35 focus:border-[#00E1D7]/60 focus:ring-2 focus:ring-[#00E1D7]/20"
                  />
                </div>

                <div className="text-[11px] text-white/45">
                  {isProbablyPhone(form.login) ? 'Detectamos telefone.' : form.login.includes('@') ? 'Detectamos e-mail.' : 'Detectamos ID.'}
                </div>
              </div>

              <div className="grid gap-1.5">
                <label className="text-xs font-semibold text-white/70">Senha</label>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={form.password}
                  onChange={(e) => setField('password', e.target.value)}
                  placeholder="••••••••"
                  className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/35 focus:border-[#00E1D7]/60 focus:ring-2 focus:ring-[#00E1D7]/20"
                />
              </div>

              <div className="flex items-center justify-between mt-1">
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3 hover:bg-white/10">
                  <input
                    type="checkbox"
                    checked={form.rememberMe}
                    onChange={(e) => setField('rememberMe', e.target.checked)}
                    className="
                      h-4 w-4 shrink-0 rounded
                      border border-white/30 bg-white/5
                      transition
                      checked:bg-[#00E1D7] checked:border-[#00E1D7]
                      focus:outline-none
                    "
                  />
                  <span className="text-sm font-bold text-white/85">Salvar login</span>
                </label>

                <button
                  type="button"
                  onClick={() => { setError(null); setStep('forgot'); }}
                  className="text-xs text-[#00e1d7] hover:underline cursor-pointer font-semibold"
                >
                  Esqueceu a senha?
                </button>
              </div>

              {/* === Sign-in com Google === */}
              {isGoogleEnabled() && (
                <>
                  <div className="my-3 flex items-center gap-3">
                    <div className="h-px flex-1 bg-white/10" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/45">
                      ou
                    </span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>
                  <div className="flex justify-center">
                    {loading ? (
                      <div className="text-xs text-white/55">Validando...</div>
                    ) : (
                      <div
                        ref={googleBtnRef}
                        className="gsi-wrap inline-block overflow-hidden rounded-lg"
                        style={{ colorScheme: 'dark' }}
                      />
                    )}
                  </div>
                  <style>{`
                    /* Suprime a borda branca/cinza grande que GSI desenha em torno
                       do botao + qualquer overlay de "user identified" do One Tap. */
                    .gsi-wrap > div, .gsi-wrap iframe { border: 0 !important; outline: 0 !important; }
                  `}</style>
                </>
              )}
            </div>
          ) : step === 'forgot' ? (
            <div className="grid gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm text-white/70 leading-relaxed">
                  Informe seu <strong className="text-white/90">ID</strong>, <strong className="text-white/90">e-mail</strong> ou <strong className="text-white/90">celular</strong>. Enviaremos um código para o celular ou e-mail cadastrado.
                </div>
              </div>

              <div className="grid gap-1.5">
                <label className="text-xs font-bold text-white/90 tracking-wide">ID, e-mail ou celular</label>
                <div className="flex items-center gap-2">
                  {isProbablyPhone(form.forgotContact) && (
                    <CountrySelector value={country} onChange={setCountry} />
                  )}
                  <input
                    value={isProbablyPhone(form.forgotContact) ? formatPhone(country, form.forgotContact) : form.forgotContact}
                    onChange={(e) => {
                      const v = e.target.value;
                      setField('forgotContact', isProbablyPhone(v) ? onlyDigits(v) : v);
                    }}
                    inputMode={isProbablyPhone(form.forgotContact) ? 'tel' : 'text'}
                    placeholder="ID, e-mail ou celular"
                    className="h-12 w-full rounded-xl border border-white/20 bg-white/8 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-[#00E1D7]/70 focus:ring-2 focus:ring-[#00E1D7]/25 hover:border-white/30 transition"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={goBackToLogin}
                className="text-xs text-white/55 hover:text-white/80 text-left"
              >
                ← Voltar ao login
              </button>
            </div>
          ) : step === 'forgot-code' ? (
            <div className="grid gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm text-white/70">
                  Enviamos um código para <strong className="text-white/90">{codeDest?.destination || forgotContactValue}</strong>.
                  {codeDest?.channel === 'email' && (
                    <div className="mt-1 text-xs text-white/50">Não chegou? Confira também a caixa de spam.</div>
                  )}
                </div>
              </div>

              <OTPInput value={otp} onChange={setOtp} disabled={loading} length={6} />

              <div className="flex items-center justify-between text-xs text-white/55">
                <button type="button" onClick={() => (renewMode ? goBackToLogin() : setStep('forgot'))} disabled={loading} className="hover:text-white/80">
                  ← Voltar
                </button>
                <button
                  type="button"
                  className={'hover:text-white/80 ' + (cooldown > 0 ? 'opacity-40 pointer-events-none' : '')}
                  onClick={doForgotRequest}
                  disabled={loading || cooldown > 0}
                >
                  {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar código'}
                </button>
              </div>
            </div>
          ) : step === 'renew' ? (
            <div className="grid gap-3">
              <div className="rounded-2xl border border-[#00e1d7]/25 bg-[#00e1d7]/5 p-4">
                <div className="text-sm text-white/80 leading-relaxed">
                  Por segurança, todas as contas antigas precisam criar uma <strong className="text-white">nova senha</strong>.
                </div>
                <div className="mt-3 text-sm text-white/70 leading-relaxed">
                  Enviamos um código para o seu {codeDest?.channel === 'email' ? 'e-mail' : 'celular'}{' '}
                  <strong className="text-white/95">{codeDest?.destination}</strong>.
                </div>
              </div>
              <button type="button" onClick={goBackToLogin} className="text-xs text-white/55 hover:text-white/80 text-left">
                ← Voltar ao login
              </button>
            </div>
          ) : step === 'forgot-newpass' ? (
            <div className="grid gap-3">
              <div className="rounded-2xl border border-[#00e1d7]/20 bg-[#00e1d7]/5 p-4">
                <div className="text-sm text-[#00e1d7] font-semibold">
                  Código verificado! Defina sua nova senha.
                </div>
              </div>

              <div className="grid gap-1.5">
                <label className="text-xs font-bold text-white/90 tracking-wide">Nova senha</label>
                <input
                  type="password"
                  value={form.newPassword}
                  onChange={(e) => setField('newPassword', e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="h-12 w-full rounded-xl border border-white/20 bg-white/8 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-[#00E1D7]/70 focus:ring-2 focus:ring-[#00E1D7]/25 hover:border-white/30 transition"
                />
              </div>

              <div className="grid gap-1.5">
                <label className="text-xs font-bold text-white/90 tracking-wide">Confirmar senha</label>
                <input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setField('confirmPassword', e.target.value)}
                  placeholder="Repita a nova senha"
                  className="h-12 w-full rounded-xl border border-white/20 bg-white/8 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-[#00E1D7]/70 focus:ring-2 focus:ring-[#00E1D7]/25 hover:border-white/30 transition"
                />
              </div>
            </div>
          ) : step === 'phone' ? (
            <div className="grid gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm font-extrabold text-white/90">ID: {loginId || '—'}</div>
                <div className="mt-2 text-sm text-white/70 leading-relaxed">
                  Para entrar usando este ID, voce precisa vincular e verificar um celular.
                </div>
              </div>

              <div className="grid gap-1.5">
                <label className="text-xs font-semibold text-white/70">Phone / Celular</label>
                <div className="flex items-center gap-2">
                  <CountrySelector
                    value={country}
                    onChange={(c) => { setCountry(c); setField('phoneDigits', ''); }}
                  />

                  <input
                    value={formatPhone(country, form.phoneDigits)}
                    onChange={(e) => setField('phoneDigits', onlyDigits(e.target.value).slice(0, country.maxDigits))}
                    inputMode="tel"
                    placeholder={phonePlaceholder(country)}
                    className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/35 focus:border-[#00E1D7]/60 focus:ring-2 focus:ring-[#00E1D7]/20"
                  />
                </div>
                <div className="text-[11px] text-white/45">We'll send an SMS code / Enviaremos um código SMS.</div>
              </div>

              <div className="flex items-center justify-between text-xs text-white/55">
                <button type="button" className="hover:text-white/80" onClick={goBackToLogin} disabled={loading}>
                  ← Voltar
                </button>
                <div className="text-white/35">Senha ja informada</div>
              </div>
            </div>
          ) : (
            <div className="grid gap-4">
              <OTPInput value={otp} onChange={setOtp} disabled={loading} length={6} />

              <div className="flex items-center justify-between text-xs text-white/55">
                <button type="button" className="hover:text-white/80" onClick={() => setStep('phone')} disabled={loading}>
                  ← Voltar
                </button>

                <button
                  type="button"
                  className={'hover:text-white/80 ' + (cooldown > 0 ? 'opacity-40 pointer-events-none' : '')}
                  onClick={resend}
                  disabled={loading || cooldown > 0}
                >
                  {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar codigo'}
                </button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-3 rounded-xl border border-[#FA0000]/30 bg-[#FA0000]/10 px-4 py-3 text-sm text-[#ffd0d0]">
            {error}
          </div>
        )}

        <DialogFooter className="mt-2">
          <button type="button" className="btn btn-dark" onClick={closeAll} disabled={loading}>
            Cancelar
          </button>

          {step === 'login' ? (
            <button type="button" className="btn btn-gold" onClick={doLogin} disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          ) : step === 'phone' ? (
            <button type="button" className="btn btn-gold" onClick={requestSmsForLink} disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar código'}
            </button>
          ) : step === 'code' ? (
            <button type="button" className="btn btn-gold" onClick={verifyAndLogin} disabled={!otpValid || loading}>
              {loading ? 'Validando...' : 'Validar e entrar'}
            </button>
          ) : step === 'forgot' ? (
            <button type="button" className="btn btn-gold" onClick={doForgotRequest} disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar código'}
            </button>
          ) : step === 'renew' ? (
            <button type="button" className="btn btn-gold" onClick={() => setStep('forgot-code')}>
              OK, digitar código
            </button>
          ) : step === 'forgot-code' ? (
            <button type="button" className="btn btn-gold" onClick={doForgotVerifyCode} disabled={!otpValid || loading}>
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
          ) : (
            <button type="button" className="btn btn-gold" onClick={doForgotSavePassword} disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar nova senha'}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
